import React from "react";
import tw from "twin.macro";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Container as ContainerBase } from "components/misc/Layouts";
import BgTitle from "components/title/BgTitle";
import CompanyLocationMobileView from "./CompanyLocationMobileView";

const Container = tw(ContainerBase)`relative`;
const TwoColumn = styled.div`
  ${tw`flex flex-col md:flex-row justify-between items-start max-w-screen-xl mx-auto py-12 md:py-20`}
`;

const Column = tw.div`w-full max-w-md mx-auto md:max-w-none md:mx-0`;
const ImageColumn = styled(Column)`
  ${tw`md:w-6/12 lg:w-5/12 flex-shrink-0`}

  iframe {
    width: 100%;
    height: 580px;
    border-radius: 12px;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);

    @media (max-width: 768px) {
      height: 280px;
    }
  }
`;

const TextColumn = styled(Column)((props) => [
  tw`md:w-6/12 mt-8 md:mt-0 flex flex-col items-center md:items-start`,
  props.textOnLeft ? tw`md:mr-8` : tw`md:ml-8`,
]);

const TextContent = tw.div`w-full`;

const AddressCard = styled.div`
  ${tw`bg-white rounded-lg shadow-md p-4 mt-4 w-full`}
  border-left: 4px solid #0c4d96;
`;

const AddressTitle = tw.h3`text-lg font-semibold text-gray-800 mb-4`;

const AddressText = styled.div`
  ${tw`text-sm leading-relaxed text-gray-700`}
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 16px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const AddressRow = styled.div`
  ${tw`w-full`}
`;

const AddressValue = tw.span`text-gray-700`;
const ContactRow = styled.div`
  ${tw`grid grid-cols-2 gap-4 mt-4`}

  @media (max-width: 640px) {
    ${tw`grid-cols-1 gap-3`}
  }
`;

const ContactColumn = styled.div`
  ${tw`flex flex-col space-y-2`}
`;

const Statistics = styled.div`
  ${tw`mt-8 grid grid-cols-1 sm:grid-cols-2 gap-6 w-full`}
`;

const Statistic = styled.div`
  ${tw`flex items-center bg-[#ffff] justify-start p-4 rounded-lg shadow-md`}
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.6);
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.05);
`;

const Icon = tw.div`text-ncc text-2xl sm:text-3xl mr-4`;
const KeyContainer = tw.div`text-left`;
const KeyBold = tw.div`text-sm md:text-base font-bold text-gray-800`;
const Key = tw.div`text-xs text-gray-600 mt-1 flex items-center`;

export default ({ isXs = false, textOnLeft = false }) => {
  const { t } = useTranslation();
  if (isXs) return <CompanyLocationMobileView />;

  const statistics = [
    {
      station: t("company_location.nearest_stations.station_1.station"),
      time: t("company_location.nearest_stations.station_1.time"),
      exit: t("company_location.nearest_stations.station_1.exit"),
    },
    {
      station: t("company_location.nearest_stations.station_2.station"),
      time: t("company_location.nearest_stations.station_2.time"),
      exit: t("company_location.nearest_stations.station_2.exit"),
    },
    {
      station: t("company_location.nearest_stations.station_3.station"),
      time: t("company_location.nearest_stations.station_3.time"),
      exit: t("company_location.nearest_stations.station_3.exit"),
    },
    {
      station: t("company_location.nearest_stations.station_4.station"),
      time: t("company_location.nearest_stations.station_4.time"),
      exit: t("company_location.nearest_stations.station_4.exit"),
    },
  ];

  return (
    <Container>
      <TwoColumn>
        {/* Map Section */}
        <ImageColumn>
          <iframe
            title="Office location map"
            src="https://www.google.com/maps/embed/v1/place?key=AIzaSyD9u_s_WGwjo88GDcuiVZSgd8RgB3JfUUk&q=%E6%9D%B1%E4%BA%AC%E9%83%BD%E5%8F%B0%E6%9D%B1%E5%8C%BA%E5%85%83%E6%B5%85%E8%8D%892-6-7%20%E3%83%9E%E3%82%BF%E3%82%A4%E3%83%93%E3%83%AB&zoom=17"
            allowfullscreen=""
            loading="lazy"
            referrerpolicy="no-referrer-when-downgrade"
          ></iframe>
        </ImageColumn>

        {/* Text Section */}
        <TextColumn textOnLeft={textOnLeft}>
          <TextContent>
            <BgTitle text={t("company_location.heading")} />

            <AddressCard>
              <AddressTitle>{t("company_location.address.title")}</AddressTitle>
              <AddressText>
                {/* Row 1: Company Title is the AddressTitle above */}

                {/* Row 2: Full Address */}
                <AddressRow>
                  <AddressValue>
                    {t("company_location.address.address_line1")}
                    <br />
                    {t("company_location.address.address_line2")}
                    <br />
                    {t("company_location.address.address_line3")}
                  </AddressValue>
                </AddressRow>
              </AddressText>

              {/* Row 3: Two columns - Phone/Fax and Emails */}
              <ContactRow>
                {/* Left Column: Phone and Fax */}
                <ContactColumn>
                  <AddressValue>
                    {t("company_location.address.phone")}
                  </AddressValue>
                  <AddressValue>
                    {t("company_location.address.fax")}
                  </AddressValue>
                </ContactColumn>

                {/* Right Column: Both Emails */}
                <ContactColumn>
                  <AddressValue>
                    {t("company_location.address.email_soumu")}
                  </AddressValue>
                  <AddressValue>
                    {t("company_location.address.email_sales")}
                  </AddressValue>
                </ContactColumn>
              </ContactRow>
            </AddressCard>
            <Statistics>
              {statistics.map((statistic, index) => (
                <Statistic key={index}>
                  <Icon>
                    <i className="fa-solid fa-train"></i>
                  </Icon>
                  <KeyContainer>
                    <KeyBold>{statistic.station}</KeyBold>
                    <Key>
                      <i className="fa-solid fa-clock mr-2"></i>&nbsp;
                      {statistic.time}
                    </Key>
                    <Key>
                      <i className="fa-solid fa-person-running mr-2"></i>&nbsp;
                      {statistic.exit}
                    </Key>
                  </KeyContainer>
                </Statistic>
              ))}
            </Statistics>
          </TextContent>
        </TextColumn>
      </TwoColumn>
    </Container>
  );
};
